import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { useQuery } from '@tanstack/react-query'
import { CmApiHttpError, cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import {
  emptyTransferStatement,
  transferStatementFromLive,
  type TransferStatementResult,
} from '@/api/map/transferStatement'

export type TransferStatementQuery = {
  from: string
  to: string
  page?: number
  pageSize?: number
  enabled?: boolean
  /** Bump to re-run Search. */
  run?: number
}

async function load(query: TransferStatementQuery): Promise<TransferStatementResult> {
  const page = Math.max(1, query.page ?? 1)
  const pageSize = Math.max(1, query.pageSize ?? DEFAULT_PAGE_SIZE)
  if (!query.from || !query.to) return emptyTransferStatement(page, pageSize)

  try {
    const live = await cmApi.transferStatementSearch(authHeaders(), {
      from: query.from,
      to: query.to,
      page,
      size: pageSize,
    })
    return transferStatementFromLive(live)
  } catch (err) {
    if (err instanceof CmApiHttpError && (err.status === 404 || err.status === 503)) {
      return emptyTransferStatement(page, pageSize)
    }
    throw err
  }
}

export function useTransferStatement(query: TransferStatementQuery) {
  return useQuery({
    queryKey: ['cm', 'transfer-statement', query],
    queryFn: () => load(query),
    enabled: Boolean(query.from && query.to && (query.enabled ?? true)),
    placeholderData: (prev) => prev,
  })
}

export async function loadTransferStatementAll(
  from: string,
  to: string,
): Promise<TransferStatementResult> {
  return load({ from, to, page: 1, pageSize: 10_000 })
}
